import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Package,
  Eye,
  Star,
  CheckCircle,
  Clock,
  Truck,
  Plus
} from 'lucide-react';
import { Product } from '../../types';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const ClientProducts: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const showPrices = user?.clientType !== 'empresa';
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [arsRate, setArsRate] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedThickness, setSelectedThickness] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');

  const categories = [
    { value: 'all', label: 'Todas las categorias' },
    { value: 'comercial', label: 'Comercial' },
    { value: 'industrial', label: 'Industrial' },
    { value: 'domestico', label: 'Domestico' }
  ];

  const thicknessOptions = [
    { value: 'all', label: 'Todos los grosores' },
    { value: '20', label: '20 micrones' },
    { value: '25', label: '25 micrones' },
    { value: '30', label: '30 micrones' },
    { value: '50', label: '50 micrones' },
    { value: '100', label: '100 micrones' },
    { value: '200', label: '200 micrones' }
  ];

  const sortOptions = [
    { value: 'name', label: 'Nombre' },
    { value: 'price', label: 'Precio' },
    { value: 'thickness', label: 'Grosor' },
    { value: 'category', label: 'Categoria' }
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [response, currency] = await Promise.all([
          apiService.getProducts(),
          apiService.getCurrencyRates().catch(() => null)
        ]);
        const fetchedProducts = response.products || [];
        setProducts(fetchedProducts);
        setFilteredProducts(fetchedProducts);
        if (currency?.rates?.sell) {
          setArsRate(Number(currency.rates.sell));
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
        setFilteredProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    let filtered = [...products];

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((product) => product.category === selectedCategory);
    }

    if (selectedThickness !== 'all') {
      filtered = filtered.filter((product) => product.thickness === parseInt(selectedThickness, 10));
    }

    if (searchTerm) {
      filtered = filtered.filter((product) =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.material.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'price':
          return (a.priceUSD || 0) - (b.priceUSD || 0);
        case 'thickness':
          return a.thickness - b.thickness;
        case 'category':
          return a.category.localeCompare(b.category);
        default:
          return 0;
      }
    });

    setFilteredProducts(filtered);
  }, [products, selectedCategory, selectedThickness, searchTerm, sortBy]);

  const getCategoryLabel = (category: string) => {
    const cat = categories.find((c) => c.value === category);
    return cat ? cat.label : category;
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'comercial':
        return 'bg-blue-100 text-blue-800';
      case 'industrial':
        return 'bg-green-100 text-green-800';
      case 'domestico':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleRequestQuote = (product: Product) => {
    navigate(`/contacto?subject=cotizacion&product=${encodeURIComponent(product.name)}`);
  };

  const getUnitsPerPack = (productName?: string) => {
    const match = String(productName || '').match(/x\s*(\d+)\s*unidades?/i);
    const units = match ? Number(match[1]) : 1;
    return Number.isFinite(units) && units > 0 ? units : 1;
  };

  const formatPackPriceARS = (product: Product) => {
    const usd = Number(product.priceUSD || 0);
    if (!usd || !arsRate) return 'Consultar precio por cantidad';
    const ars = usd * getUnitsPerPack(product.name) * arsRate;
    return `$${Math.round(ars).toLocaleString('es-AR')} ARS por pack`;
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow">
              <div className="h-4 bg-gray-300 rounded w-3/4 mb-4"></div>
              <div className="h-3 bg-gray-300 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-300 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Catalogo de Productos</h1>
          <p className="text-gray-600">Explora los productos cargados por administracion</p>
        </div>
        <div className="text-sm text-gray-500">{filteredProducts.length} productos disponibles</div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar productos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {categories.map((category) => (
              <option key={category.value} value={category.value}>{category.label}</option>
            ))}
          </select>

          <select
            value={selectedThickness}
            onChange={(e) => setSelectedThickness(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {thicknessOptions.map((thickness) => (
              <option key={thickness.value} value={thickness.value}>{thickness.label}</option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>Ordenar por: {option.label}</option>
            ))}
          </select>

          <button className="flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
            <Filter className="h-5 w-5 mr-2" />
            Filtros
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProducts.map((product) => (
          <div key={product.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <Package className="h-5 w-5 text-primary-600 mr-2" />
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(product.category)}`}>
                      {getCategoryLabel(product.category)}
                    </span>
                    {product.isFeatured && <Star className="h-4 w-4 text-yellow-500 ml-2" />}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{product.name}</h3>
                </div>
              </div>

              <p className="text-gray-600 text-sm mb-4 line-clamp-3">{product.description}</p>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm"><span className="text-gray-500">Dimensiones:</span><span className="font-medium">{Math.round(Number(product.width) * 100)} cm x {Math.round(Number(product.length) * 100)} cm</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">Grosor:</span><span className="font-medium">{product.thickness} micrones</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">Material:</span><span className="font-medium">{product.material}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">Minimo:</span><span className="font-medium">{product.minQuantity.toLocaleString()} unidades</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">Entrega:</span><span className="font-medium">{Number(product.deliveryDays) === 0 ? 'Inmediata' : `${product.deliveryDays} dias`}</span></div>
              </div>

              <div className="mb-4 border-t pt-4">
                {showPrices && product.showPrice ? (
                  <p className="text-2xl font-bold text-primary-600">{formatPackPriceARS(product)}</p>
                ) : (
                  <p className="text-sm font-medium text-gray-500">Precio especial acordado — consultá tu cotización</p>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleRequestQuote(product)}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Cotizar
                </button>
                <button className="flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                  <Eye className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredProducts.length === 0 && (
        <div className="text-center py-12">
          <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay productos cargados</h3>
          <p className="text-gray-600">El administrador aun no cargo productos en el catalogo.</p>
        </div>
      )}

      <div className="bg-primary-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-primary-900 mb-4">Informacion Importante</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center"><CheckCircle className="h-5 w-5 text-green-600 mr-3" /><div><p className="text-sm font-medium text-gray-900">Calidad Garantizada</p><p className="text-xs text-gray-600">Material LDPE de primera calidad</p></div></div>
          <div className="flex items-center"><Clock className="h-5 w-5 text-blue-600 mr-3" /><div><p className="text-sm font-medium text-gray-900">Entrega Rapida</p><p className="text-xs text-gray-600">7 a 15 dias habiles</p></div></div>
          <div className="flex items-center"><Truck className="h-5 w-5 text-purple-600 mr-3" /><div><p className="text-sm font-medium text-gray-900">Envio a Domicilio</p><p className="text-xs text-gray-600">San Juan y alrededores</p></div></div>
        </div>
      </div>
    </div>
  );
};

export default ClientProducts;

